import { useState } from 'react';

import { controlClass } from '@/components/Field';
import { Button, OnBandSurface } from '@/components/PageHeading';
import { newBookingSteps, stepCard, stepTitle } from '@core/bookings/newBookingSteps';
import type { NewBookingStep } from '@core/bookings/newBookingSteps';
import type { BookingDraft } from '@core/bookings/draft';

const help: Record<NewBookingStep, string> = {
  when: 'Pick the day. Collect and return fall either side; change them if the customer asks.',
  customer: 'The person paying. The looks can be for anyone in their party.',
  looks: 'Choose the person, then a garment. It goes in the bag for them.',
  bag: 'What goes out, and the money. Everything has a default — change only what the customer asks.',
};

/**
 * The band across the top of a new booking (canvas "Booking flow: customer after dates", 2026-11-05):
 * the step's question in the serif, and the four steps as cards — each saying what has been chosen so
 * far, a step already passed one click back, the steps ahead shown but not yet open.
 */
export function StepBand({
  step,
  draft,
  onStep,
  onClose,
}: {
  step: NewBookingStep;
  draft: BookingDraft;
  onStep: (step: NewBookingStep) => void;
  onClose: () => void;
}) {
  const at = newBookingSteps.indexOf(step);

  return (
    <OnBandSurface>
      <div className="page-band mb-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <p className="page-eyebrow">Bookings · New booking</p>
            <h1 className="page-title">{stepTitle[step]}</h1>
            <p className="page-subtitle mb-0">{help[step]}</p>
          </div>
          <Button icon="icon icon-x" onClick={onClose} aria-label="Close the new booking">
            Close
          </Button>
        </div>

        <ol className="m-0 mt-4 grid list-none grid-cols-1 gap-2 ps-0 sm:grid-cols-2 xl:grid-cols-4">
          {newBookingSteps.map((each, index) => {
            const current = index === at;
            const card = stepCard(each, step, draft);

            return (
              <li key={each} className="list-none">
                <button
                  type="button"
                  disabled={index > at}
                  aria-current={current ? 'step' : undefined}
                  onClick={() => onStep(each)}
                  className={`flex w-full items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-start disabled:cursor-default ${
                    current
                      ? 'cursor-default bg-[#ffffff]'
                      : 'cursor-pointer bg-[#ffffff]/10 hover:bg-[#ffffff]/15 disabled:hover:bg-[#ffffff]/10'
                  }`}
                >
                  <span
                    className={`flex size-[26px] shrink-0 items-center justify-center rounded-full text-[13px] font-bold ${
                      current
                        ? 'bg-primary text-[#ffffff]'
                        : card.done
                          ? 'bg-[#34D399] text-(--color-hero)'
                          : 'border border-[#ffffff]/50 text-[#ffffff]'
                    }`}
                    aria-hidden="true"
                  >
                    {card.done ? <i className="icon icon-check text-[13px]" /> : index + 1}
                  </span>
                  <span className="flex min-w-0 flex-col">
                    <span
                      className={`truncate font-semibold ${current ? 'text-(--color-hero)' : 'text-[#ffffff]'}`}
                    >
                      {card.title}
                    </span>
                    <span
                      // Written out: this card is white in both themes, and `text-gray-900` flips in dark.
                      className={`truncate text-[12px] ${current ? 'text-[#4B5563]' : 'text-(--color-hero-muted)'}`}
                    >
                      {card.detail}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </OnBandSurface>
  );
}

/**
 * Who the next garment goes to, asked first — "The customer", everyone named since, and + Add a
 * person (the phone's `WhoWears`), each with how many garments they have. Somebody new is chosen at
 * once, so the next pick goes to them.
 */
export function WhoWears({
  people,
  forKey,
  busy,
  onForChange,
  onAddPerson,
}: {
  people: { key: string; label: string; relation: string; garments: number }[];
  forKey: string;
  busy: boolean;
  onForChange: (key: string) => void;
  onAddPerson: (name: string, relation: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [relation, setRelation] = useState('');

  const commit = () => {
    if (!name.trim()) {
      return;
    }

    onAddPerson(name.trim(), relation.trim());
    setName('');
    setRelation('');
    setAdding(false);
  };

  const enter = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="me-1 text-[13px] font-semibold text-gray-900">For</span>
      {people.map((person) => {
        const on = person.key === forKey;

        return (
          <button
            key={person.key}
            type="button"
            disabled={busy}
            aria-pressed={on}
            onClick={() => onForChange(person.key)}
            className={`inline-flex h-11 cursor-pointer items-center gap-2 rounded-full border ps-1.5 pe-3.5 text-[13px] ${
              on
                ? 'border-primary bg-primary text-white'
                : 'border-border-color bg-white text-gray-900 hover:bg-light'
            }`}
          >
            <span
              className={`flex size-7 items-center justify-center rounded-full text-[12px] font-bold ${
                on ? 'text-primary bg-white' : 'bg-primary-transparent text-primary'
              }`}
              aria-hidden="true"
            >
              {person.label.trim()[0]?.toUpperCase() ?? '?'}
            </span>
            <span className="flex flex-col items-start leading-tight">
              <span className="font-semibold">{person.label}</span>
              <span className="text-[11px] opacity-85">
                {person.relation ? `${person.relation} · ` : ''}
                {person.garments === 0
                  ? 'nothing yet'
                  : `${person.garments} garment${person.garments === 1 ? '' : 's'}`}
              </span>
            </span>
          </button>
        );
      })}

      {adding ? (
        <span className="flex flex-wrap items-center gap-1.5">
          <input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={enter}
            placeholder="Their name"
            aria-label="Their name"
            className={`${controlClass} h-10 w-40`}
          />
          <input
            value={relation}
            onChange={(event) => setRelation(event.target.value)}
            onKeyDown={enter}
            placeholder="Brother, friend… (optional)"
            aria-label="Who they are"
            className={`${controlClass} h-10 w-52`}
          />
          <Button variant="primary" disabled={!name.trim()} onClick={commit}>
            Add them
          </Button>
          <Button onClick={() => setAdding(false)}>Cancel</Button>
        </span>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => setAdding(true)}
          className="border-border-color h-11 cursor-pointer rounded-full border border-dashed bg-white px-3.5 text-[13px] text-gray-900 hover:bg-light"
        >
          + Add a person
        </button>
      )}
    </div>
  );
}
